import { useEffect } from 'react';

import { useForm, useFormState } from 'react-final-form';

import {
  Button,
  DrawerContent,
  DrawerFooter,
  Icons,
  useDrawerInstance,
  useSwipeableViews,
  Stack,
} from '@cast/design-system';

import { PROVISION_NODES_CUSTOM_INITIAL_VALUES } from '../constants';
import { useProvisionNodesDrawerContext } from '../ProvisionNodesDrawerContext';
import { ProvisionNodesCustomConfigurationFormValues } from '../types';
import { ProvisionNodesCustomConfigurationSlideForm } from './ProvisionNodesCustomConfigurationSlideForm';
import { validate } from './validate.schema';

import { RffForm } from '@cast/shared-rff-ui';
import { EnhancedEdgeLocation } from '../../../types';

const ConfigurationSlideFooter = () => {
  const { close, setOnCloseGuard, setShouldRenderGuard } = useDrawerInstance();
  const form = useForm<ProvisionNodesCustomConfigurationFormValues>();
  const { valid, dirty } = useFormState({
    subscription: { valid: true, dirty: true },
  });

  // Enable guard when form is dirty
  useEffect(() => {
    setOnCloseGuard(dirty);
  }, [dirty, setOnCloseGuard, setShouldRenderGuard]);

  const handleClickNext = () => {
    form.submit();
  };

  const handleCancel = () => {
    if (dirty) {
      setShouldRenderGuard(true);
    } else {
      close();
    }
  };

  return (
    <DrawerFooter sx={{ justifyContent: 'flex-end' }}>
      <Stack direction="row" gap={16}>
        <Button
          variant="secondary"
          size="md"
          onClick={handleCancel}
          testId="cancel-button"
        >
          Cancel
        </Button>
        <Button
          size="md"
          endIcon={<Icons.ArrowRight />}
          disabled={!valid}
          onClick={handleClickNext}
          testId="next-button"
        >
          Next
        </Button>
      </Stack>
    </DrawerFooter>
  );
};

type Props = {
  edgeLocation: EnhancedEdgeLocation;
};

export const ProvisionNodesCustomConfigurationSlide = ({
  edgeLocation,
}: Props) => {
  const { customConfigValue, setCustomConfigValue } =
    useProvisionNodesDrawerContext();
  const swipeableViews = useSwipeableViews();

  const handleSubmit = (
    values: ProvisionNodesCustomConfigurationFormValues
  ) => {
    setCustomConfigValue(values);
    swipeableViews?.goToNextSlide();
  };

  return (
    <RffForm<ProvisionNodesCustomConfigurationFormValues>
      mode="create"
      array
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      initialValues={customConfigValue ?? PROVISION_NODES_CUSTOM_INITIAL_VALUES}
      validate={validate}
      onSubmit={handleSubmit}
    >
      <DrawerContent sx={{ flex: 1 }}>
        <ProvisionNodesCustomConfigurationSlideForm
          edgeLocation={edgeLocation}
        />
      </DrawerContent>
      <ConfigurationSlideFooter />
    </RffForm>
  );
};
